import type { FileUploadContexts } from "@/enums/file-upload-contexts"
import { HTTPMethods, HTTPRequestBuilder } from "@/builder/HTTPRequestBuilder"
import type { FileBucket } from "@/models/file-bucket.model"
import { FileRelation } from "@/models/file/file-relation.model"
import type { AxiosResponse } from "axios"
import type { IFileRelation } from "@/interfaces/api/file-relation.interface.ts"
import type { IRepositoryRequestConfig } from "@/interfaces/repository/repository-request-config.interface.ts"

export class FileBucketRepository {
  public uploadToFileBucket(
    fileBucket: FileBucket,
    fileData: File,
    fileUploadContext: FileUploadContexts,
    config: IRepositoryRequestConfig = {}
  ) {
    const formData = new FormData()
    formData.append("file", fileData)
    formData.append("context", fileUploadContext.toString())

    return new HTTPRequestBuilder(HTTPMethods.POST, `core/file-buckets/${fileBucket.id}/upload/`)
      .setData(formData)
      .setExpectedStatusCode(201)
      .query(config.query)
      .addStatusCodeHandlers(config.statusCodeHandlers)
      .setCloning(false)
      .build()
      .execute()
  }

  /**
   * UGLY FALLBACK METHOD, TODO remove this when we fixed the CapacitorHttp issue on iOS.
   * @param fileBucket
   * @param fileData
   * @param fileUploadContext
   * @param config
   */
  public async uploadToFileBucketBase64(
    fileBucket: FileBucket,
    fileData: File,
    fileUploadContext: FileUploadContexts,
    config: IRepositoryRequestConfig = {}
  ): Promise<AxiosResponse<IFileRelation>> {
    return new Promise<AxiosResponse<IFileRelation>>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = async (event) => {
        try {
          const base64Data = event.target?.result
          const payload = {
            file: base64Data,
            context: fileUploadContext.toString()
          }
          const response = await new HTTPRequestBuilder(
            HTTPMethods.POST,
            `core/file-buckets/${fileBucket.id}/upload/`
          )
            .setData(payload)
            .setExpectedStatusCode(201)
            .query(config.query)
            .addStatusCodeHandlers(config.statusCodeHandlers)
            .build()
            .execute()
          resolve(response)
        } catch (fallbackError) {
          reject(fallbackError)
        }
      }
      reader.onerror = (err) => {
        reject(err)
      }
      reader.readAsDataURL(fileData)
    })
  }

  /**
   * Link a file relation (containing an already uploaded file) to a file bucket.
   * @param fileRelation
   * @param fileBucket
   */
  public async linkFileRelationToBucket(fileRelation: FileRelation, fileBucket: FileBucket) {
    return await new HTTPRequestBuilder(
      HTTPMethods.POST,
      `core/file-buckets/${fileBucket.id}/file-relations/`
    )
      .setData(fileRelation)
      .setExpectedStatusCode(201)
      .build()
      .execute()
  }
}
